import React, { useState, useEffect } from "react";
import {
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  makeStyles,
  FormControl,
  Box,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  countrySelect: {
    minWidth: 120,
    "& .MuiSelect-root": {
      display: "flex",
      alignItems: "center",
    },
  },
  phoneInput: {
    flex: 1,
  },
  flag: {
    marginRight: theme.spacing(0.5),
    fontSize: "1.1rem",
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

// Common country codes - Israel first as default
const countryCodes = [
  { code: "972", country: "Israel", flag: "🇮🇱", format: "5X XXX XXXX" },
  { code: "1", country: "USA/Canada", flag: "🇺🇸", format: "XXX XXX XXXX" },
  { code: "44", country: "UK", flag: "🇬🇧", format: "XXXX XXX XXX" },
  { code: "49", country: "Germany", flag: "🇩🇪", format: "XXX XXXXXXX" },
  { code: "33", country: "France", flag: "🇫🇷", format: "X XX XX XX XX" },
  { code: "39", country: "Italy", flag: "🇮🇹", format: "XXX XXX XXXX" },
  { code: "34", country: "Spain", flag: "🇪🇸", format: "XXX XX XX XX" },
  { code: "31", country: "Netherlands", flag: "🇳🇱", format: "X XXXX XXXX" },
  { code: "32", country: "Belgium", flag: "🇧🇪", format: "XXX XX XX XX" },
  { code: "41", country: "Switzerland", flag: "🇨🇭", format: "XX XXX XX XX" },
  { code: "43", country: "Austria", flag: "🇦🇹", format: "XXX XXXXXXX" },
  { code: "380", country: "Ukraine", flag: "🇺🇦", format: "XX XXX XX XX" },
  { code: "7", country: "Russia", flag: "🇷🇺", format: "XXX XXX XX XX" },
  { code: "48", country: "Poland", flag: "🇵🇱", format: "XXX XXX XXX" },
  { code: "90", country: "Turkey", flag: "🇹🇷", format: "XXX XXX XXXX" },
  { code: "30", country: "Greece", flag: "🇬🇷", format: "XXX XXX XXXX" },
  { code: "357", country: "Cyprus", flag: "🇨🇾", format: "XX XXXXXX" },
  { code: "971", country: "UAE", flag: "🇦🇪", format: "XX XXX XXXX" },
  { code: "55", country: "Brazil", flag: "🇧🇷", format: "XX XXXXX XXXX" },
  { code: "52", country: "Mexico", flag: "🇲🇽", format: "XX XXXX XXXX" },
  { code: "91", country: "India", flag: "🇮🇳", format: "XXXXX XXXXX" },
  { code: "86", country: "China", flag: "🇨🇳", format: "XXX XXXX XXXX" },
  { code: "81", country: "Japan", flag: "🇯🇵", format: "XX XXXX XXXX" },
  { code: "82", country: "South Korea", flag: "🇰🇷", format: "XX XXXX XXXX" },
  { code: "61", country: "Australia", flag: "🇦🇺", format: "XXX XXX XXX" },
  { code: "64", country: "New Zealand", flag: "🇳🇿", format: "XX XXX XXXX" },
  { code: "27", country: "South Africa", flag: "🇿🇦", format: "XX XXX XXXX" },
  { code: "20", country: "Egypt", flag: "🇪🇬", format: "XX XXXX XXXX" },
  { code: "212", country: "Morocco", flag: "🇲🇦", format: "XXX XXXXXX" },
  { code: "962", country: "Jordan", flag: "🇯🇴", format: "X XXXX XXXX" },
  { code: "961", country: "Lebanon", flag: "🇱🇧", format: "XX XXX XXX" },
];

/**
 * PhoneInputWithCountry component
 * 
 * @param {Object} props
 * @param {string} props.value - Full phone number (with or without country code)
 * @param {function} props.onChange - Callback when phone number changes (receives full number with country code)
 * @param {string} props.label - Input label
 * @param {string} props.error - Error message
 * @param {boolean} props.touched - Whether field was touched
 * @param {string} props.helperText - Helper text
 * @param {string} props.variant - TextField variant
 * @param {string} props.margin - TextField margin
 * @param {boolean} props.disabled - Whether input is disabled
 */
const PhoneInputWithCountry = ({
  value = "",
  onChange,
  label = "Phone Number",
  error,
  touched,
  helperText,
  variant = "outlined",
  margin = "dense",
  disabled = false,
  name = "number",
}) => {
  const classes = useStyles();
  
  // Parse initial value to extract country code and local number
  const parsePhoneNumber = (fullNumber) => {
    if (!fullNumber) return { countryCode: "972", localNumber: "" };
    
    // Clean the number - remove all non-digits
    const cleaned = fullNumber.replace(/\D/g, "");
    
    // Try to match known country codes (longest first)
    const sortedCodes = [...countryCodes].sort((a, b) => b.code.length - a.code.length);
    
    for (const country of sortedCodes) {
      if (cleaned.startsWith(country.code)) {
        return {
          countryCode: country.code,
          localNumber: cleaned.slice(country.code.length),
        };
      }
    }
    
    // If starts with 0, assume it's a local Israeli number
    if (cleaned.startsWith("0")) {
      return {
        countryCode: "972",
        localNumber: cleaned.slice(1), // Remove leading 0
      };
    }
    
    // Default to Israel with the number as local
    return {
      countryCode: "972",
      localNumber: cleaned,
    };
  };
  
  const parsed = parsePhoneNumber(value);
  const [countryCode, setCountryCode] = useState(parsed.countryCode);
  const [localNumber, setLocalNumber] = useState(parsed.localNumber);
  
  // Update local state when value prop changes
  useEffect(() => {
    const parsed = parsePhoneNumber(value);
    setCountryCode(parsed.countryCode);
    setLocalNumber(parsed.localNumber);
  }, [value]);
  
  const handleCountryChange = (event) => {
    const newCode = event.target.value;
    setCountryCode(newCode);
    
    // Notify parent with combined number
    if (onChange) {
      const fullNumber = localNumber ? `${newCode}${localNumber}` : "";
      onChange({
        target: {
          name,
          value: fullNumber,
        },
      });
    }
  };
  
  const handleNumberChange = (event) => {
    // Clean input - allow only digits
    let input = event.target.value.replace(/\D/g, "");
    
    // Remove leading 0 if present (common in local format)
    if (input.startsWith("0")) {
      input = input.slice(1);
    }
    
    setLocalNumber(input);
    
    // Notify parent with combined number
    if (onChange) {
      const fullNumber = input ? `${countryCode}${input}` : "";
      onChange({
        target: {
          name,
          value: fullNumber,
        },
      });
    }
  };
  
  const selectedCountry = countryCodes.find((c) => c.code === countryCode) || countryCodes[0];
  
  return (
    <Box className={classes.container}>
      <FormControl variant={variant} margin={margin} className={classes.countrySelect}>
        <Select
          value={countryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          renderValue={(selected) => {
            const country = countryCodes.find((c) => c.code === selected);
            return (
              <span>
                <span className={classes.flag}>{country?.flag}</span>
                +{selected}
              </span>
            );
          }}
        >
          {countryCodes.map((country) => (
            <MenuItem key={country.code} value={country.code}>
              <div className={classes.menuItem}>
                <span className={classes.flag}>{country.flag}</span>
                <span>{country.country}</span>
                <span style={{ color: "#666", marginLeft: "auto" }}>+{country.code}</span>
              </div>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <TextField
        className={classes.phoneInput}
        value={localNumber}
        onChange={handleNumberChange}
        label={label}
        variant={variant}
        margin={margin}
        disabled={disabled}
        error={touched && Boolean(error)}
        helperText={(touched && error) || helperText}
        placeholder={selectedCountry.format}
        InputProps={{
          startAdornment: localNumber && countryCode === "972" && !localNumber.startsWith("5") ? (
            <InputAdornment position="start" style={{ color: "#f44336" }}>
              ⚠️
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  );
};

export default PhoneInputWithCountry;














